import { useCallback, useEffect, useRef, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { formatDateTime } from '@/utils/date'
import { Button } from '@/components/ui/button'
import { OWNERS_CHANGED } from '@/pages/admin/OwnerPicker'
import type { AiOwner } from '@/pages/admin/OwnerPicker'

// AI-OWNERS-1 — people who can be assigned AI risks and actions. They do not
// need a login. Nothing is deleted (an owner is made inactive). "Add from
// staff" only fills the form; it never saves.

interface Report { owners: AiOwner[]; active_count: number }
interface StaffUser { email: string; name: string; role: string }

const BASE = '/v1/admin/ai-owners'
const STAFF_ROLES = ['super_admin', 'cpa', 'analyst', 'valuation_signer']
const blank = { name: '', email: '', title: '' }

function errorText(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string }
  const detail = e?.response?.data?.detail
  return typeof detail === 'string' ? detail : e?.message || 'Save failed.'
}

function OwnerRow({ o, error, onSave }: {
  o: AiOwner
  error?: string
  onSave: (id: string, patch: { name: string; email: string; title: string; active: boolean }) => void
}) {
  const [d, setD] = useState({ name: o.name, email: o.email, title: o.title, active: o.active })
  // Reset only when the saved values change, never on mount (see AiIncidentsSection).
  const seen = useRef({ name: o.name, email: o.email, title: o.title, active: o.active })
  useEffect(() => {
    const next = { name: o.name, email: o.email, title: o.title, active: o.active }
    const s = seen.current
    if (next.name === s.name && next.email === s.email && next.title === s.title && next.active === s.active) return
    seen.current = next
    setD(next)
  }, [o.name, o.email, o.title, o.active])
  return (
    <tr data-testid={`ow-row-${o.id}`} className={`border-t align-top ${d.active ? '' : 'text-gray-400'}`}>
      <td className="py-2 pr-2"><input type="text" className="w-40 rounded border px-2 py-1" value={d.name}
        onChange={e => setD({ ...d, name: e.target.value })} /></td>
      <td className="py-2 pr-2"><input type="text" className="w-56 rounded border px-2 py-1" value={d.email}
        onChange={e => setD({ ...d, email: e.target.value })} /></td>
      <td className="py-2 pr-2"><input type="text" className="w-40 rounded border px-2 py-1" value={d.title}
        onChange={e => setD({ ...d, title: e.target.value })} /></td>
      <td className="py-2 pr-2">
        <input type="checkbox" data-testid={`ow-active-${o.id}`} checked={d.active}
          onChange={e => setD({ ...d, active: e.target.checked })} />
      </td>
      <td className="py-2 pr-2">
        <Button type="button" size="sm" data-testid={`ow-save-${o.id}`} onClick={() => onSave(o.id, d)}>Save</Button>
        <span className="mt-1 block text-xs text-gray-500">
          Updated {formatDateTime(o.updated_at ?? '')} by {o.updated_by}
        </span>
        {error && <span data-testid={`ow-error-${o.id}`} className="mt-1 block text-sm text-red-600">{error}</span>}
      </td>
    </tr>
  )
}

export function AiOwnersSection() {
  const { data, loading, error, execute } = useApi<Report>()
  const [report, setReport] = useState<Report | null>(null)
  const [form, setForm] = useState(blank)
  const [formError, setFormError] = useState('')
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  const [staff, setStaff] = useState<StaffUser[]>([])

  const load = useCallback(
    () => execute(() => apiClient.get<Report>(BASE).then(res => res.data)),
    [execute],
  )
  useEffect(() => { load() }, [load])
  useEffect(() => { if (data) setReport(data) }, [data])
  useEffect(() => {
    Promise.resolve()
      .then(() => apiClient.get<{ users?: StaffUser[] }>('/v1/admin/users'))
      .then(res => setStaff(Array.isArray(res?.data?.users) ? res.data.users : []))
      .catch(() => setStaff([]))
  }, [])

  const owners = report?.owners ?? []
  const listed = new Set(owners.map(o => o.email.toLowerCase()))
  const choices = staff.filter(u => STAFF_ROLES.includes(u.role) && !listed.has(u.email.toLowerCase()))
  const saved = (r: Report) => { setReport(r); window.dispatchEvent(new Event(OWNERS_CHANGED)) }

  const add = async () => {
    setFormError('')
    try {
      const res = await apiClient.post<Report>(BASE, {
        name: form.name.trim(), email: form.email.trim(), title: form.title.trim(),
      })
      saved(res.data)
      setForm(blank)
    } catch (err) {
      setFormError(errorText(err))
    }
  }

  const save = async (id: string, patch: { name: string; email: string; title: string; active: boolean }) => {
    setRowErrors(prev => ({ ...prev, [id]: '' }))
    try {
      saved((await apiClient.put<Report>(`${BASE}/${id}`, patch)).data)
    } catch (err) {
      setRowErrors(prev => ({ ...prev, [id]: errorText(err) }))
    }
  }

  const fromStaff = (email: string) => {
    const u = staff.find(s => s.email === email)
    if (u) setForm({ name: u.name || '', email: u.email, title: u.role.replace(/_/g, ' ') })
  }

  return (
    <section data-testid="ai-owners-section" className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">AI owners</h2>
      <p className="text-sm text-gray-600">People who can be assigned AI risks and actions. They do not need a login.</p>
      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading && !report && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="flex flex-wrap items-start gap-2">
        <select data-testid="ow-staff" className="rounded border px-2 py-1" value=""
          onChange={e => fromStaff(e.target.value)}>
          <option value="">Add from staff</option>
          {choices.map(u => <option key={u.email} value={u.email}>{u.name || u.email} - {u.email}</option>)}
        </select>
        <input type="text" data-testid="ow-name" placeholder="Name" maxLength={120}
          className="w-40 rounded border px-2 py-1" value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })} />
        <input type="text" data-testid="ow-email" placeholder="Email" className="w-56 rounded border px-2 py-1"
          value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        <input type="text" data-testid="ow-title" placeholder="Title" maxLength={120}
          className="w-40 rounded border px-2 py-1" value={form.title}
          onChange={e => setForm({ ...form, title: e.target.value })} />
        <Button type="button" size="sm" data-testid="ow-add" onClick={add}>Add</Button>
      </div>
      {formError && <p data-testid="ow-error" className="text-sm text-red-600">{formError}</p>}

      {owners.length === 0 ? (
        report && <p data-testid="ow-empty" className="text-sm text-gray-500">No owners yet. Until you add one, owners are typed as an email.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-600"><th>Name</th><th>Email</th><th>Title</th><th>Active</th><th /></tr>
          </thead>
          <tbody>
            {owners.map(o => <OwnerRow key={o.id} o={o} error={rowErrors[o.id]} onSave={save} />)}
          </tbody>
        </table>
      )}
    </section>
  )
}
